/**
 * Regras de densidade de rótulos por nível de aproximação.
 *
 * Usamos a RESOLUÇÃO da view (metros da projeção GOES por pixel de tela) em
 * vez do número do zoom, porque o número depende de como o app-core configura
 * a view. Referências (projeção GOES, perto do Brasil ≈ metros no terreno):
 * - Amazônia Legal inteira numa tela de ~1400 px ≈ 2000 m/px;
 * - resolução máxima da imagem do setor NSA (7200 px) ≈ 1000 m/px.
 *
 * Sedes (`nivel`: 1 = capital, 2 = ≥100 mil hab., 3 = ≥30 mil, 4 = demais):
 * - zoom baixo  (res > 1400 m/px): capitais e ≥100 mil  (nível ≤ 2)
 * - zoom médio  (res > 600 m/px):  + ≥30 mil            (nível ≤ 3)
 * - zoom alto   (res ≤ 600 m/px):  todas                (nível ≤ 4)
 *
 * A "densidade" do usuário desloca os limites: +1 (mais nomes) mostra cada
 * nível com metade da aproximação; -1 (menos nomes) exige o dobro.
 */

/** Limites de resolução (m/px) para mostrar os níveis 3 e 4, com densidade normal. */
export const SEDE_THRESHOLDS = {
  /** Abaixo desta resolução aparecem as cidades de nível 3. */
  nivel3: 1400,
  /** Abaixo desta resolução aparecem todas as cidades (nível 4). */
  nivel4: 600,
} as const;

/** Fator aplicado aos limites conforme a densidade escolhida. */
export function densityFactor(density: number): number {
  const d = Math.max(-1, Math.min(1, Math.round(density || 0)));
  return 2 ** d;
}

/** Maior `nivel` de sede que deve ter rótulo nesta resolução. */
export function maxNivelForResolution(resolution: number, density = 0): 2 | 3 | 4 {
  const f = densityFactor(density);
  if (resolution <= SEDE_THRESHOLDS.nivel4 * f) return 4;
  if (resolution <= SEDE_THRESHOLDS.nivel3 * f) return 3;
  return 2;
}

/** A sede de nível `nivel` tem rótulo nesta resolução? Nível inválido conta como 4. */
export function isSedeLabelVisible(nivel: unknown, resolution: number, density = 0): boolean {
  const n = typeof nivel === 'number' && nivel >= 1 && nivel <= 4 ? nivel : 4;
  return n <= maxNivelForResolution(resolution, density);
}

/** Rios: nomes dos rios de ordem 3 (menores) só com mais aproximação. */
export function maxRiverOrderForLabels(resolution: number): number {
  if (resolution <= 900) return 3;
  if (resolution <= 2500) return 2;
  return 1;
}

/**
 * Rótulo de polígono (TI, UC) só se o polígono ocupar pelo menos `minPx`×`minPx`
 * pixels de tela — evita nomes em áreas minúsculas no zoom afastado.
 */
export function polygonBigEnough(areaMapUnits: number, resolution: number, minPx = 36): boolean {
  return areaMapUnits / (resolution * resolution) >= minPx * minPx;
}
